import { MouseEvent } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CompanySmall } from 'api';
import { dashboardCompanyOperationsValuationUrl, WORKBOARD_CURRENT_MODELS_URL, WORKBOARD_PAST_MODELS_URL } from 'dashboard409a/common/config/urls';
import { DASHBOARD_WORKBOARD_SLUG } from 'dashboard409a/common/constants';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import { 
  findStandaloneCompanyForValuation, 
  formatMeasurementDate, 
  generateCompanyOperationsUrl 
} from 'hooks/useCompanyLookup';

// Key for storing the previous workboard route in localStorage
const WORKBOARD_PREVIOUS_ROUTE_KEY = 'workboard_previous_route';

/**
 * Handles row click navigation for valuation tables
 * @param row - The valuation row that was clicked
 * @param event - The mouse event
 * @param navigate - React Router navigate function
 * @param standaloneCompanies - Array of standalone companies for lookup
 */
export const handleValuationRowClick = (
  row: SimplifiedAttioValuation,
  event: MouseEvent<HTMLTableRowElement>,
  navigate: NavigateFunction,
  standaloneCompanies: CompanySmall[] = []
) => {
  // Prevent navigation if clicking on input elements
  if (event.target instanceof HTMLInputElement) {
    return;
  }

  // Save current route to localStorage before navigating
  // This allows us to return to the exact workboard page when going back
  if (globalThis.window) {
    const currentPath = globalThis.window.location.pathname;
    // Only save if we're coming from a workboard route
    if (currentPath.includes(DASHBOARD_WORKBOARD_SLUG)) {
      globalThis.window.localStorage.setItem(WORKBOARD_PREVIOUS_ROUTE_KEY, currentPath);
    }
  }

  // Find the matching standalone company for this Attio valuation
  const matchingCompany = findStandaloneCompanyForValuation(row, standaloneCompanies);
  
  if (matchingCompany) {
    // Generate the proper URL with company slug and ID
    const measurementDate = formatMeasurementDate(row.valuation_date);
    const url = generateCompanyOperationsUrl(matchingCompany, row.record_id, measurementDate);
    navigate(url);
  } else {
    // Fallback to the old behavior if no matching company is found
    const fallbackSlug = 'unknown-company';
    const fallbackUrl = dashboardCompanyOperationsValuationUrl(fallbackSlug, row.record_id);
    navigate(fallbackUrl);
  }
};

export const  handleModelRowClick = (
  model: ModelWithValuationData,
  event: MouseEvent<HTMLTableRowElement>,
  navigate: NavigateFunction
) => {
  // Check if event.target has closest method (real DOM element) before using it
  const target = event.target as HTMLElement;
  const hasClosest = typeof target.closest === 'function';
  
  if (target instanceof HTMLInputElement || 
      target instanceof HTMLButtonElement ||
      target instanceof HTMLSelectElement ||
      (hasClosest && (target.closest('button') || target.closest('input') || target.closest('select')))) {
    return;
  }

  // Save current route to localStorage before navigating
  // This allows us to return to the exact workboard page when going back
  if (globalThis.window) {
    const currentPath = globalThis.window.location.pathname;
    // Only save if we're coming from a workboard route
    if (currentPath.includes(DASHBOARD_WORKBOARD_SLUG)) {
      globalThis.window.localStorage.setItem(WORKBOARD_PREVIOUS_ROUTE_KEY, currentPath);
    }
  }

  if (model.company_slug && model.attio_valuation_id) {
    const url = dashboardCompanyOperationsValuationUrl(model.company_slug, model.attio_valuation_id);
    // Check if we're coming from a models workboard page to add hash for scroll
    if (globalThis.window) {
      const currentPath = globalThis.window.location.pathname;
      if (currentPath.includes(WORKBOARD_CURRENT_MODELS_URL) || currentPath.includes(WORKBOARD_PAST_MODELS_URL)) {
        // Add hash to URL to scroll to Model Status section
        navigate(`${url}#model-status`);
        return;
      }
    }
    navigate(url);
  }
};

/**
 * Generates the fallback URL for unknown companies
 * @param valuationId - The valuation record ID
 * @returns The fallback URL string
 */
export const generateFallbackValuationUrl = (valuationId: string): string => {
  const fallbackSlug = 'unknown-company';
  return dashboardCompanyOperationsValuationUrl(fallbackSlug, valuationId);
};

/**
 * Gets the previous workboard route from localStorage
 * @returns The previous route path or null if not found
 */
export const getPreviousWorkboardRoute = (): string | null => {
  if (!globalThis.window) {
    return null;
  }
  return globalThis.window.localStorage.getItem(WORKBOARD_PREVIOUS_ROUTE_KEY);
};

/**
 * Clears the previous workboard route from localStorage
 */
export const clearPreviousWorkboardRoute = (): void => {
  if (globalThis.window) {
    globalThis.window.localStorage.removeItem(WORKBOARD_PREVIOUS_ROUTE_KEY);
  }
};
